import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { ipdApi } from '@/api/ipd';
import type { IPDPackageType } from '@/types/ipd';
import { IPD_PACKAGE_TYPE_OPTIONS } from '@/lib/ipdPackageTypes';

export type AdmissionPackageValues = {
  surgeryName?: string | null;
  totalPackageCost?: number | null;
  packageType?: IPDPackageType | null;
  gstApplicable?: boolean;
};

type Props = {
  admissionId: string;
  initial: AdmissionPackageValues;
  /** Called after a successful save (e.g. invalidate queries). */
  onSaved?: () => void;
  /** Show GST checkbox (admission form style). */
  showGst?: boolean;
  className?: string;
};

export default function AdmissionPackageEditor({
  admissionId,
  initial,
  onSaved,
  showGst = true,
  className,
}: Props) {
  const queryClient = useQueryClient();
  const [surgeryName, setSurgeryName] = useState('');
  const [packageCost, setPackageCost] = useState('');
  const [packageType, setPackageType] = useState<IPDPackageType>('PACKAGE_1');
  const [gstApplicable, setGstApplicable] = useState(false);

  useEffect(() => {
    setSurgeryName(initial.surgeryName ?? '');
    setPackageCost(
      initial.totalPackageCost != null && Number(initial.totalPackageCost) > 0
        ? String(initial.totalPackageCost)
        : ''
    );
    setPackageType(initial.packageType ?? 'PACKAGE_1');
    setGstApplicable(!!initial.gstApplicable);
  }, [
    admissionId,
    initial.surgeryName,
    initial.totalPackageCost,
    initial.packageType,
    initial.gstApplicable,
  ]);

  const saveMut = useMutation({
    mutationFn: async () => {
      const hasPackage = packageCost !== '' && Number(packageCost) > 0;
      const payload: Record<string, unknown> = {
        surgeryName: surgeryName.trim() || null,
        gstApplicable,
      };
      if (hasPackage) {
        payload.totalPackageCost = parseFloat(packageCost);
        payload.packageType = packageType;
      } else {
        payload.totalPackageCost = null;
        payload.packageType = null;
      }
      return ipdApi.updateAdmission(admissionId, payload as any);
    },
    onSuccess: () => {
      toast.success('Package details updated');
      void queryClient.invalidateQueries({ queryKey: ['ipd-admission', admissionId] });
      void queryClient.invalidateQueries({ queryKey: ['ipd-bill-details', admissionId] });
      onSaved?.();
    },
    onError: (e: { response?: { data?: { message?: string } } }) => {
      toast.error(e.response?.data?.message || 'Failed to update package');
    },
  });

  const hasPackage = packageCost !== '' && Number(packageCost) > 0;
  const selectedOption = IPD_PACKAGE_TYPE_OPTIONS.find((o) => o.value === packageType);

  return (
    <div className={className}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-muted-foreground">Surgery name</Label>
          <Input
            className="mt-1 h-9"
            value={surgeryName}
            onChange={(e) => setSurgeryName(e.target.value)}
            placeholder="e.g. Rhinoplasty, liposuction"
          />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Total package cost (₹)</Label>
          <Input
            type="number"
            min={0}
            step={0.01}
            className="mt-1 h-9"
            value={packageCost}
            onChange={(e) => {
              const v = e.target.value;
              setPackageCost(v);
              if (v !== '' && Number(v) > 0 && !packageType) {
                setPackageType('PACKAGE_1');
              }
            }}
            placeholder="Leave empty if not a package patient"
          />
        </div>
      </div>

      {hasPackage && (
        <div className="mt-3">
          <Label className="text-xs text-muted-foreground">Package type</Label>
          <Select value={packageType} onValueChange={(v: IPDPackageType) => setPackageType(v)}>
            <SelectTrigger className="mt-1 h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {IPD_PACKAGE_TYPE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedOption && (
            <p className="text-xs text-muted-foreground mt-1">{selectedOption.description}</p>
          )}
        </div>
      )}

      {showGst && (
        <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50/80 p-3 mt-3">
          <Checkbox
            id={`gst-${admissionId}`}
            checked={gstApplicable}
            onCheckedChange={(checked) => setGstApplicable(checked === true)}
            disabled={saveMut.isPending}
          />
          <div className="space-y-1">
            <Label htmlFor={`gst-${admissionId}`} className="text-sm font-medium cursor-pointer">
              GST applicable (5%)
            </Label>
            <p className="text-xs text-muted-foreground">
              Adds 5% GST on the final IPD bill when required (e.g. plastic surgery).
            </p>
          </div>
        </div>
      )}

      <p className="text-xs text-muted-foreground mt-2">
        Excluded items are billed separately. After changing package type, re-run accrual if the bill is
        still in DRAFT.
      </p>

      <Button
        type="button"
        size="sm"
        className="mt-3"
        disabled={saveMut.isPending}
        onClick={() => saveMut.mutate()}
      >
        {saveMut.isPending ? 'Saving…' : 'Save package details'}
      </Button>
    </div>
  );
}
